﻿import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useProviderFavorite } from '../../contexts/ProviderFavoriteContext';
import {
  Bookmark,
  Briefcase,
  Calendar,
  CheckCircle2,
  ChevronDown,
  Clock,
  FileText,
  Filter,
  MapPin,
  Search,
  Send,
  ShieldCheck,
  Star,
  Wallet,
} from 'lucide-react';
import {
  manproQueryKeys,
  mapProviderBidToRow,
  mapProviderOpportunityToCard,
  providerBidService,
  providerOpportunityService,
  taxonomyService,
  type BidStatus,
  type PaginatedResponse,
  type ProviderOpportunityDto,
  type ProviderOpportunityParams,
  type ProjectSource,
  type ProcurementType,
} from '../../api/manpro';
import { getOpportunityAction } from '../../lib/manproOpportunityMedia';

const categoryOptions = [
  'Semua Kategori',
  'Bangun Gedung',
  'Renovasi Rumah',
  'Interior',
  'MEP / Elektrikal',
  'Konstruksi Sipil',
  'Maintenance',
  'Facility Project',
];

const budgetRangeOptions = [
  'Semua Budget',
  'Di bawah Rp 100 Juta',
  'Rp 100 Juta - Rp 500 Juta',
  'Rp 500 Juta - Rp 1 Miliar',
  'Rp 1 Miliar - Rp 5 Miliar',
  'Di atas Rp 5 Miliar',
];

const sortOptions = [
  { label: 'Terbaru', value: 'latest' },
  { label: 'Deadline Terdekat', value: 'deadline' },
  { label: 'Nilai Tertinggi', value: 'budget_high' },
  { label: 'Nilai Terendah', value: 'budget_low' },
];

const isPaginatedResponse = (
  payload?: PaginatedResponse<ProviderOpportunityDto> | ProviderOpportunityDto[] | null,
): payload is PaginatedResponse<ProviderOpportunityDto> =>
  Boolean(payload && !Array.isArray(payload) && Array.isArray(payload.data));

const getOpportunityRows = (
  payload?: PaginatedResponse<ProviderOpportunityDto> | ProviderOpportunityDto[] | null,
) => {
  if (Array.isArray(payload)) return payload;
  return payload?.data ?? [];
};

const isActiveBidStatus = (status?: BidStatus) =>
  status === 'pending' || status === 'submitted' || status === 'accepted';

const ProviderOpportunities = () => {
  const [activeProcurementType, setActiveProcurementType] = useState('');
  const [activeProjectSource, setActiveProjectSource] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [location, setLocation] = useState('');
  const [category, setCategory] = useState('');
  const [budgetRange, setBudgetRange] = useState('');
  const [sort, setSort] = useState('latest');
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();
  const { favoriteIds: bookmarkedIds, toggleFavorite, isToggling } = useProviderFavorite();
  const taxonomyQuery = useQuery({
    queryKey: manproQueryKeys.taxonomy(),
    queryFn: taxonomyService.getTaxonomy,
    staleTime: 6 * 60 * 60 * 1000,
  });
  const procurementTypeOptions = [{ label: 'Semua Tipe', value: '' }, ...(taxonomyQuery.data?.procurement_types ?? [])];
  const projectSourceOptions = [{ label: 'Semua Sumber', value: '' }, ...(taxonomyQuery.data?.project_sources ?? [])];

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 450);

    return () => window.clearTimeout(timeoutId);
  }, [search]);

  const queryParams = useMemo<ProviderOpportunityParams>(
    () => ({
      offset: page,
      limit: 10,
      search: debouncedSearch || undefined,
      location: location || undefined,
      category: category || undefined,
      budget_range: budgetRange || undefined,
      procurement_type: (activeProcurementType || undefined) as ProcurementType | undefined,
      project_source: (activeProjectSource || undefined) as ProjectSource | undefined,
      sort,
    }),
    [activeProcurementType, activeProjectSource, budgetRange, category, debouncedSearch, location, page, sort],
  );

  const opportunitiesQuery = useQuery({
    queryKey: manproQueryKeys.providerOpportunities(queryParams),
    // Data utama Peluang Provider harus selalu datang dari endpoint MANPRO, bukan lagi dari prototype Owner.
    queryFn: () => providerOpportunityService.getProviderOpportunities(queryParams),
  });

  const providerBidsQuery = useQuery({
    queryKey: manproQueryKeys.providerBids({ limit: 200 }),
    // Fallback sinkronisasi status bid: jika payload opportunity belum membawa has_bid terbaru,
    // data Penawaran Saya tetap menjadi sumber pembanding agar tombol Kirim Bid/Lihat Bid akurat.
    queryFn: () => providerBidService.getProviderBids({ limit: 200 }),
  });

  const rows = getOpportunityRows(opportunitiesQuery.data);
  const activeBidByProjectKey = useMemo(() => {
    const payload = providerBidsQuery.data;
    const bidRows = (Array.isArray(payload) ? payload : payload?.data ?? []).map(mapProviderBidToRow);
    const map = new Map<string, { status: BidStatus; id: string }>();

    bidRows
      .filter((bid) => isActiveBidStatus(bid.status))
      .forEach((bid) => {
        if (bid.ownerProjectId) map.set(String(bid.ownerProjectId), { status: bid.status, id: bid.id });
        if (bid.postId) map.set(String(bid.postId), { status: bid.status, id: bid.id });
      });

    return map;
  }, [providerBidsQuery.data]);

  const opportunities = useMemo(
    () =>
      rows.map((item) => {
        const opportunity = mapProviderOpportunityToCard(item);
        const activeBid =
          activeBidByProjectKey.get(String(opportunity.ownerProjectId || '')) ||
          activeBidByProjectKey.get(String(opportunity.id)) ||
          activeBidByProjectKey.get(String(opportunity.postId || ''));

        if (!activeBid) return opportunity;

        return {
          ...opportunity,
          hasBid: true,
          providerBidStatus: activeBid.status,
        };
      }),
    [activeBidByProjectKey, rows],
  );
  const pagination = isPaginatedResponse(opportunitiesQuery.data) ? opportunitiesQuery.data : null;

  const locationOptions = useMemo(
    () => [
      'Semua Lokasi',
      ...Array.from(
        new Set(
          // Opsi filter lokasi harus memakai field mentah dari API.
          // Jangan ambil dari mapped opportunity.location karena itu label gabungan untuk card.
          rows
            .map((opportunity) => opportunity.location || opportunity.city || opportunity.province || opportunity.district)
            .filter((value): value is string => Boolean(value)),
        ),
      ),
    ],
    [rows],
  );

  const totalOpportunities = pagination?.total ?? opportunities.length;
  const currentPage = pagination?.current_page ?? page;
  const lastPage = pagination?.last_page ?? 1;
  const submittedBidCount = opportunities.filter((opportunity) => opportunity.hasBid).length;
  const closingSoonCount = opportunities.filter((opportunity) => !opportunity.isDeadlinePassed).length;

  const toggleBookmark = (projectId: string) => {
    toggleFavorite(projectId);
  };

  const resetFilters = () => {
    setActiveProcurementType('');
    setActiveProjectSource('');
    setSearch('');
    setDebouncedSearch('');
    setLocation('');
    setCategory('');
    setBudgetRange('');
    setSort('latest');
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <section>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Peluang Proyek Provider</h1>
        <p className="mt-2 text-sm text-slate-500">
          Temukan opportunity dari Portal Owner, review scope pekerjaan, lalu kirim bid provider.
        </p>
      </section>

      <section className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-500">
              <Search size={17} />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="w-full bg-transparent text-slate-700 outline-none placeholder:text-slate-400"
                placeholder="Cari proyek, lokasi, kategori..."
              />
            </label>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-500">
              {opportunitiesQuery.isLoading
                ? 'Memuat opportunity provider...'
                : `Menampilkan ${opportunities.length} dari ${totalOpportunities} opportunity terbuka`}
            </p>
          </div>

          {opportunitiesQuery.isError && (
            <div className="rounded-2xl border border-rose-100 bg-rose-50 p-5 text-sm font-semibold text-rose-700">
              Gagal memuat peluang provider. Pastikan akun Anda memiliki akses provider dan coba muat ulang halaman.
            </div>
          )}

          <div className="space-y-4">
            {opportunitiesQuery.isLoading &&
              Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={`provider-opportunity-skeleton-${index}`}
                  className="h-48 animate-pulse rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className="grid h-full gap-4 lg:grid-cols-[150px_minmax(0,1fr)_170px_160px]">
                    <div className="rounded-xl bg-slate-100" />
                    <div className="space-y-3">
                      <div className="h-5 w-32 rounded bg-slate-100" />
                      <div className="h-6 w-3/4 rounded bg-slate-100" />
                      <div className="h-4 w-full rounded bg-slate-100" />
                      <div className="h-4 w-2/3 rounded bg-slate-100" />
                    </div>
                    <div className="rounded-xl bg-slate-100" />
                    <div className="rounded-xl bg-slate-100" />
                  </div>
                </div>
              ))}

            {!opportunitiesQuery.isLoading &&
              opportunities.map((project) => {
                const isBookmarked = bookmarkedIds.includes(project.id);

                return (
                  <article
                    key={project.id}
                    className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-blue-100 hover:shadow-md"
                  >
                    <div className="grid gap-4 lg:grid-cols-[150px_minmax(0,1fr)_170px_160px] lg:items-stretch">
                      <img src={project.coverImage} alt={project.title} className="h-36 w-full rounded-xl object-cover lg:h-full" />

                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-extrabold text-blue-700">
                            {project.procurementType}
                          </span>
                          <span className="rounded-full bg-violet-50 px-2.5 py-1 text-xs font-extrabold text-violet-700">
                            {project.sourceLabel}
                          </span>
                          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-extrabold text-slate-600">
                            {project.category}
                          </span>
                          {project.isDeadlinePassed ? (
                            <span className="rounded-full bg-rose-50 px-2.5 py-1 text-xs font-extrabold text-rose-700">
                              Bid Ditutup
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-extrabold text-emerald-700">
                              <ShieldCheck size={13} />
                              Terbuka
                            </span>
                          )}
                        </div>
                        <h2 className="mt-3 line-clamp-2 text-lg font-extrabold leading-snug text-slate-900">{project.title}</h2>
                        <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-500">{project.description}</p>
                        {project.procurementMethod && (
                          <p className="mt-1 text-xs font-semibold text-slate-500">Metode: {project.procurementMethod}</p>
                        )}
                        <div className="mt-3 grid gap-2 text-xs font-semibold text-slate-500 sm:grid-cols-2">
                          <span className="inline-flex min-w-0 items-center gap-2">
                            <MapPin size={14} className="text-slate-400" />
                            <span className="truncate">{project.location}</span>
                          </span>
                          <span className="inline-flex min-w-0 items-center gap-2">
                            <Briefcase size={14} className="text-slate-400" />
                            <span className="truncate">{project.ownerName}</span>
                          </span>
                        </div>
                      </div>

                      <div className="border-t border-slate-100 pt-3 text-sm lg:border-l lg:border-t-0 lg:pl-5 lg:pt-0">
                        <p className="font-extrabold text-slate-800">Budget Owner</p>
                        <p className="mt-2 line-clamp-1 text-sm font-extrabold text-slate-900">{project.budgetLabel}</p>
                        <p className="mt-3 flex items-center gap-2 text-xs font-semibold text-slate-500">
                          <Calendar size={14} />
                          Deadline: {project.deadlineLabel}
                        </p>
                        <p className="mt-2 flex items-center gap-2 text-xs font-semibold text-slate-500">
                          <FileText size={14} />
                          {project.documentCount} dokumen
                        </p>
                      </div>

                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`rounded-full px-2.5 py-1 text-xs font-extrabold ${
                            project.isRedirectOpportunity
                              ? 'bg-violet-50 text-violet-700'
                              : project.hasBid
                                ? 'bg-blue-50 text-blue-700'
                                : 'bg-amber-50 text-amber-700'
                          }`}>
                            {project.isRedirectOpportunity
                              ? 'Redirect LPSE'
                              : project.hasBid
                                ? `Bid ${project.providerBidStatus || 'submitted'}`
                                : 'Belum bid'}
                          </span>
                          <button
                            onClick={() => toggleBookmark(project.id)}
                            disabled={isToggling}
                            className={`shrink-0 rounded-xl p-2 transition ${
                              isBookmarked ? 'bg-blue-50 text-blue-600' : 'text-slate-400 hover:bg-slate-50 hover:text-blue-600'
                            }`}
                            type="button"
                            aria-label="Simpan opportunity"
                          >
                            <Bookmark size={18} className={isBookmarked ? 'fill-blue-600' : ''} />
                          </button>
                        </div>
                        {project.isRedirectOpportunity ? (
                          <a
                            href={project.sourceUrl || '#'}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-extrabold text-white transition hover:bg-blue-700"
                          >
                            <Send size={16} />
                            {getOpportunityAction(project, `/provider-opportunities/${project.id}`).label}
                          </a>
                        ) : project.hasBid || project.isDeadlinePassed ? (
                          <Link
                            to={project.hasBid ? '/provider-bids' : `/provider-opportunities/${project.id}`}
                            className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-extrabold transition ${
                              project.hasBid
                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                : 'bg-slate-100 text-slate-500'
                            }`}
                          >
                            <Wallet size={16} />
                            {project.hasBid ? 'Lihat Bid' : 'Bid Ditutup'}
                          </Link>
                        ) : (
                          <Link
                            to={`/provider-opportunities/${project.id}/bid`}
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-extrabold text-white transition hover:bg-blue-700"
                          >
                            <Send size={16} />
                            Kirim Bid
                          </Link>
                        )}
                        <Link
                          to={`/provider-opportunities/${project.id}`}
                          className="rounded-xl border border-slate-200 px-4 py-2.5 text-center text-sm font-extrabold text-blue-700 transition hover:bg-blue-50"
                        >
                          Lihat Detail
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })}

            {!opportunitiesQuery.isLoading && opportunities.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
                <Clock size={34} className="mx-auto text-slate-300" />
                <p className="mt-3 font-bold text-slate-700">Belum ada opportunity terbuka</p>
                <p className="mt-1 text-sm text-slate-400">Coba ubah filter atau tunggu owner memasang proyek baru.</p>
              </div>
            )}
          </div>

          {lastPage > 1 && (
            <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
              <button
                onClick={() => setPage((previous) => Math.max(previous - 1, 1))}
                disabled={currentPage <= 1}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
                type="button"
              >
                Sebelumnya
              </button>
              {Array.from({ length: lastPage }).slice(0, 7).map((_, index) => {
                const pageNumber = index + 1;

                return (
                  <button
                    key={pageNumber}
                    onClick={() => setPage(pageNumber)}
                    className={`h-10 w-10 rounded-xl text-sm font-extrabold ${
                      currentPage === pageNumber
                        ? 'bg-blue-600 text-white'
                        : 'border border-slate-200 bg-white text-slate-600'
                    }`}
                    type="button"
                  >
                    {pageNumber}
                  </button>
                );
              })}
              <button
                onClick={() => setPage((previous) => Math.min(previous + 1, lastPage))}
                disabled={currentPage >= lastPage}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
                type="button"
              >
                Selanjutnya
              </button>
            </div>
          )}
        </div>

        <aside className="w-full space-y-4 lg:sticky lg:top-24 lg:w-[320px]">
          <div className="h-fit rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-extrabold text-slate-900">Filter Pencarian</h2>
                <p className="mt-1 text-xs font-semibold text-slate-400">Persempit peluang sesuai target bid.</p>
              </div>
              <Filter size={21} className="text-blue-600" />
            </div>

            <div className="mt-5 space-y-4">
              <FilterSelect
                label="Lokasi"
                value={location}
                placeholder="Semua Lokasi"
                options={locationOptions}
                onChange={(value) => {
                  setLocation(value === 'Semua Lokasi' ? '' : value);
                  setPage(1);
                }}
              />
              <FilterSelect
                label="Kategori"
                value={category}
                placeholder="Semua Kategori"
                options={categoryOptions}
                onChange={(value) => {
                  setCategory(value === 'Semua Kategori' ? '' : value);
                  setPage(1);
                }}
              />
              <FilterSelect
                label="Budget"
                value={budgetRange}
                placeholder="Semua Budget"
                options={budgetRangeOptions}
                onChange={(value) => {
                  setBudgetRange(value === 'Semua Budget' ? '' : value);
                  setPage(1);
                }}
              />
              <FilterSelect
                label="Sumber Proyek"
                value={activeProjectSource}
                placeholder="Semua Sumber"
                options={projectSourceOptions.map((item) => item.label)}
                onChange={(label) => {
                  const selected = projectSourceOptions.find((item) => item.label === label);
                  setActiveProjectSource(selected?.value || '');
                  setPage(1);
                }}
              />
              <FilterSelect
                label="Tipe Pengadaan"
                value={activeProcurementType}
                placeholder="Semua Tipe"
                options={procurementTypeOptions.map((item) => item.label)}
                onChange={(label) => {
                  const selected = procurementTypeOptions.find((item) => item.label === label);
                  setActiveProcurementType(selected?.value || '');
                  setPage(1);
                }}
              />
              <FilterSelect
                label="Urutkan"
                value={sort}
                placeholder="Terbaru"
                options={sortOptions.map((item) => item.label)}
                onChange={(label) => {
                  const selected = sortOptions.find((item) => item.label === label);
                  setSort(selected?.value || 'latest');
                  setPage(1);
                }}
              />

              <button
                onClick={resetFilters}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-extrabold text-blue-700 transition hover:bg-blue-50"
                type="button"
              >
                Reset Filter
              </button>

              <div className="grid grid-cols-2 gap-3 border-t border-slate-100 pt-4">
                {[
                  ['Terbuka', totalOpportunities],
                  ['Sudah Bid', submittedBidCount],
                  ['Bisa Dibid', closingSoonCount],
                  ['Disimpan', bookmarkedIds.length],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-xl bg-slate-50 p-3">
                    <p className="text-lg font-extrabold text-slate-900">{value}</p>
                    <p className="mt-1 text-[11px] font-bold text-slate-500">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5">
            <Star size={28} className="fill-amber-400 text-amber-400" />
            <h2 className="mt-3 font-extrabold text-slate-900">Tips Menang Bid</h2>
            <div className="mt-4 space-y-3 text-sm font-semibold text-slate-600">
              {['Lengkapi metode kerja', 'Cantumkan durasi realistis', 'Berikan opsi value engineering'].map((item) => (
                <p key={item} className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  {item}
                </p>
              ))}
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
};

type FilterSelectProps = {
  label: string;
  value: string;
  placeholder: string;
  options: string[];
  onChange: (value: string) => void;
};

const FilterSelect = ({ label, value, placeholder, options, onChange }: FilterSelectProps) => {
  const selectedValue = value || placeholder;

  return (
    <label className="block space-y-2">
      <span className="text-sm font-bold text-slate-700">{label}</span>
      <span className="relative block">
        <select
          value={selectedValue}
          onChange={(event) => onChange(event.target.value)}
          className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-9 text-sm font-semibold text-slate-600 outline-none transition hover:border-blue-200 focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
        >
          {options.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
        <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
      </span>
    </label>
  );
};

export default ProviderOpportunities;

